import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail, PACKAGE_TYPES } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const EMPTY = {
  sender_name: "", sender_address: "", receiver_name: "", receiver_address: "",
  origin: "", destination: "", weight: "", package_type: "Carton",
  service_tier: "Standard", declared_value: "", customer_email: "", notes: "",
};

export const ShipmentFormModal = ({ open, onOpenChange, onSaved, initial }) => {
  const editing = Boolean(initial);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        weight: parseFloat(form.weight) || 0,
        declared_value: parseFloat(form.declared_value) || 0,
      };
      let data;
      if (editing) {
        ({ data } = await api.put(`/shipments/${initial.shipment_id}`, payload));
        toast.success("Shipment updated");
      } else {
        ({ data } = await api.post("/shipments", payload));
        toast.success(`Shipment created · ${data.tracking_number}`);
      }
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-slate-900 text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {editing ? "Edit Shipment" : "Create Shipment"}
          </DialogTitle>
        </DialogHeader>
        <form data-testid={ADMIN.form} onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Sender Name" required>
              <Input data-testid={ADMIN.senderName} required value={form.sender_name} onChange={set("sender_name")} className="bg-slate-800 border-white/10" />
            </Field>
            <Field label="Receiver Name" required>
              <Input data-testid={ADMIN.receiverName} required value={form.receiver_name} onChange={set("receiver_name")} className="bg-slate-800 border-white/10" />
            </Field>
            <Field label="Sender Address">
              <Input data-testid={ADMIN.senderAddress} value={form.sender_address} onChange={set("sender_address")} className="bg-slate-800 border-white/10" />
            </Field>
            <Field label="Receiver Address">
              <Input data-testid={ADMIN.receiverAddress} value={form.receiver_address} onChange={set("receiver_address")} className="bg-slate-800 border-white/10" />
            </Field>
            <Field label="Origin" required>
              <Input data-testid={ADMIN.origin} required value={form.origin} onChange={set("origin")} className="bg-slate-800 border-white/10" />
            </Field>
            <Field label="Destination" required>
              <Input data-testid={ADMIN.destination} required value={form.destination} onChange={set("destination")} className="bg-slate-800 border-white/10" />
            </Field>
            <Field label="Weight (kg)">
              <Input data-testid={ADMIN.weight} type="number" step="0.1" value={form.weight} onChange={set("weight")} className="bg-slate-800 border-white/10" />
            </Field>
            <Field label="Declared Value">
              <Input type="number" value={form.declared_value} onChange={set("declared_value")} className="bg-slate-800 border-white/10" />
            </Field>
            <Field label="Package Type">
              <Select value={form.package_type} onValueChange={setVal("package_type")}>
                <SelectTrigger data-testid={ADMIN.packageType} className="bg-slate-800 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PACKAGE_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Service Tier">
              <Select value={form.service_tier} onValueChange={setVal("service_tier")}>
                <SelectTrigger data-testid={ADMIN.serviceTier} className="bg-slate-800 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Standard", "Express", "Priority"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Customer Email (link to a customer account)">
            <Input data-testid={ADMIN.customerEmail} type="email" value={form.customer_email} onChange={set("customer_email")} placeholder="customer@godeliver.com" className="bg-slate-800 border-white/10" />
          </Field>
          <Field label="Operator Notes">
            <Textarea data-testid={ADMIN.notes} value={form.notes} onChange={set("notes")} className="bg-slate-800 border-white/10" />
          </Field>
          <Button data-testid={ADMIN.submit} type="submit" disabled={saving} className="w-full bg-orange-600 text-white hover:bg-orange-700">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {editing ? "Save Changes" : "Create Shipment & Generate Tracking No."}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">
      {label}{required && <span className="text-orange-500"> *</span>}
    </Label>
    {children}
  </div>
);
